import type { DependencyMapNode } from './types';
import { isTruthy } from '@ardoq/common-helpers';
import type { ArdoqId } from '@ardoq/api-types';
import { referencedNodes } from './utils';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import type { EnhancedScopeData } from '@ardoq/data-model';

const getComponentIdMaxChildDepth = (scopeData: EnhancedScopeData) => {
  const componentIdMaxChildDepth = (
    currentDepth: number,
    currentComponentId: ArdoqId
  ): number => {
    const childIds = enhancedScopeDataOperations.getChildrenIds(
      scopeData,
      currentComponentId
    );
    if (!childIds.length) {
      return currentDepth;
    }
    return Math.max(
      ...childIds.map(childId =>
        componentIdMaxChildDepth(currentDepth + 1, childId)
      )
    );
  };
  return componentIdMaxChildDepth;
};

const getReferenceChildMaxDepth = (scopeData: EnhancedScopeData) => {
  const componentIdMaxChildDepth = getComponentIdMaxChildDepth(scopeData);
  const referenceChildMaxDepth = (node: DependencyMapNode): number => {
    // potential optimization: first collect all ids and filter to a unique set, then calculate max depth (avoid checking the same component id multiple times)
    const referencedComponentIds = [
      ...(node.incomingReferences?.map(
        ({ referenceIds }) =>
          enhancedScopeDataOperations.getReference(scopeData, referenceIds[0])
            ?.source
      ) ?? []),
      ...(node.outgoingReferences?.map(
        ({ referenceIds }) =>
          enhancedScopeDataOperations.getReference(scopeData, referenceIds[0])
            ?.target
      ) ?? []),
    ];
    return Math.max(
      ...referencedComponentIds
        .filter(isTruthy)
        .map(referencedComponentId =>
          componentIdMaxChildDepth(0, referencedComponentId)
        ),
      ...Array.from(referencedNodes(node)).map(referenceChildMaxDepth),
      ...(node.children ? node.children.map(referenceChildMaxDepth) : []),
      0
    );
  };
  return referenceChildMaxDepth;
};
export { getReferenceChildMaxDepth };
